// React & React Hooks
import React, { useEffect, useState, useContext } from "react";
import { useDispatch } from "react-redux";
// Third-party libraries
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import moment from "moment";
// MUI components
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { styled } from "@mui/material/styles";
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined";
// Custom hooks
import useDebounce from "../custom-hook/useDebounce";
import dataConstant from "../../constants/dataConstants";
import urlConstant from "../../constants/urlConstants";
import { httpPost } from "../../services/commonService";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import SnackState from "../../shared-components/snackbar/snackState";
import { isLoading } from "../../redux/slice/userSlice";

const CustomTextFieldLeft = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "var(--inputs)",
    color: "var(--black)",
    fontSize: "0.9rem",
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    minHeight: "40px",
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "1rem",
    color: "var(--black)",
    marginBottom: theme.spacing(0.5),
  },
}));

const MediaCardGrid = ({
  title = null,
  defaultItemsPerPage = 9,
  enableSearch = true,
  onDataChange = null,
  actionButtons = [],
  onFetchData = null,
  value = 0,
  mediaType = null,
  pdfButtons = [],
  onBack,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [isSearchText, setIsSearchText] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isFirstloading, setIsFirstLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const authenticationState = useContext(AuthenticationContext);
  const debouncedSearch = useDebounce(search, 1000);
  const [buttonQualificationID, setButtonQualificationID] = useState(0);
  const [buttonURL, setButtonURL] = useState(null);
  const [buttonName, setButtonName] = useState(null);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const dispatch = useDispatch();
  useEffect(() => {
    if (!onFetchData || !value) return;
    const fetchData = async () => {
      setLoading(true);
      dispatch(isLoading({ isLoading: true }));
      try {
        const result = await onFetchData({
          page: currentPage,
          pageSize: defaultItemsPerPage,
          search,
        });

        setData(result.data);
        const [firstMedia] = result?.data ?? [];
        if (firstMedia) {
          setButtonQualificationID(
            firstMedia.ButtonQualificationID || buttonQualificationID
          );
          setButtonURL(firstMedia.ButtonURL || buttonURL);
          setButtonName(firstMedia.ButtonName || null);
        }
        setTotalCount(result.totalCount);
        onDataChange?.(result.data);
      } catch (error) {
        console.error("Data fetch error:", error);
      } finally {
        setLoading(false);
        dispatch(isLoading({ isLoading: false }));
        setIsFirstLoading(true);
      }
    };
    fetchData();
  }, [currentPage, value, isSearchText, onDataChange]);

  useEffect(() => {
    if (isFirstloading) {
      setCurrentPage(1);
      setIsSearchText(!isSearchText);
    }
  }, [debouncedSearch]);

  const totalPages = Math.max(
    1,
    Math.ceil(totalCount / Math.max(1, defaultItemsPerPage))
  );

  const handleCompleteSurvey = () => {
    if (
      buttonQualificationID !== null &&
      buttonQualificationID !== dataConstant.Undefined
    ) {
      const assignQualificationURL =
        urlConstant.MediaPageDetails.AssignMediaPageQualification;
      const model = { QualificationID: buttonQualificationID };
      httpPost(
        assignQualificationURL,
        model,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      )
        .then((response) => {
          if (
            response.Value.Status === dataConstant.APIResponseStatus.Success            
          ) {
            showSnackbar(response.Value.Message, "success");
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          }
        })
        .catch((err) => {
          console.error(err);          
        });
    }
    if (buttonURL !== null && buttonURL !== dataConstant.Undefined) {
      window.open(buttonURL, "_blank");
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  return (
    <>
      {title && (
        <Typography variant="h4" fontWeight={600}>
          {title}
        </Typography>
      )}

      {enableSearch && (
        <Stack
          className="customfield-label-left cm-normal-search"
          sx={{
            maxWidth: "500px",
            width: "100%",
            flexDirection: "row",
            alignItems: "flex-end",
          }}
        >
          <Card className="customfield-label-inner">
            <SearchOutlinedIcon />
          </Card>
          <CustomTextFieldLeft
            fullWidth
            placeholder={t("Common.Search")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            variant="outlined"
          />
        </Stack>
      )}

      {/* Grid Layout */}
      {!loading && (
        <>
          <Grid container spacing={2} width={"100%"}>
            {isFirstloading && !loading && data.length === 0 && (
              <Alert
                sx={(theme) => ({
                  background: theme.palette.common.errorbg,
                  color: theme.palette.common.errortext,
                  marginBottom: "5px",
                  width: "100%",
                })}
                variant="filled"
                severity="info"
              >
                {t("Media.No media available")}
              </Alert>
            )}
            {data.map((row, rowIndex) => {
              return (
                <React.Fragment key={row.Id}>
                  {mediaType === dataConstant.MediaCategoryTypes.Picture && (
                    <Grid key={rowIndex} item size={{ xs: 12, sm: 6, lg: 4 }}>
                      <Card
                        key={rowIndex}
                        sx={(theme) => ({
                          background: theme.palette.common.black,
                          borderRadius: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 0,
                          width: "100%",
                          flexDirection: "column",
                          height: "100%",
                        })}
                      >
                        <Stack
                          direction={"row"}
                          gap={1}
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"flex-end"}
                          flexWrap={"wrap"}
                          p={"16px 20px"}
                          borderBottom={"1px solid var(--headerbg)"}
                          minHeight={{ xs: "auto", sm: "73px" }}
                        >
                          {row.DisplayTrainingDate && (
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={2}
                            >
                              <Card
                                sx={(theme) => ({
                                  background: theme.palette.secondary.headerbg,
                                  borderRadius: "10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 0,
                                  width: "100%",
                                  flexDirection: "column",
                                  height: "100%",
                                  p: 1,
                                })}
                              >
                                <img src="/icons/calendar.svg" alt="calendar" />
                              </Card>

                              <Typography
                                color="secondary.text"
                                variant="body1"
                              >
                                {moment(row.TrainingDate || new Date()).format(
                                  "MM/DD/YYYY"
                                )}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>

                        {row.MediaURL ? (
                          <a
                            href={row.MediaURL}
                            className="banner cursor-pointer"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              className="gallery-image-card"
                              width={"100%"}
                              src={
                                row.StoredPath !== null
                                  ? `${row.StoredPath}`
                                  : `${process.env.REACT_APP_BLOB_URL}/backoffice-assets/NoImage.png`
                              }
                              alt="user"
                            />
                          </a>
                        ) : (
                          <img
                            className="gallery-image-card"
                            width={"100%"}
                            src={
                              row.StoredPath !== null
                                ? `${row.StoredPath}`
                                : `${process.env.REACT_APP_BLOB_URL}/backoffice-assets/NoImage.png`
                            }
                            alt="user"
                          />
                        )}
                        <Stack
                          direction={"row"}
                          sx={{ gap: { xs: 1, sm: 4 } }}
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          p={"16px 20px"}
                          minHeight={{ xs: "auto", sm: "75px" }}
                          borderTop={"1px solid var(--headerbg)"}
                        >
                          <Typography variant="body1" sx={{wordBreak:"break-word"}} color="secondary.text">
                            {row.MediaDescription}
                          </Typography>
                          {row.DownloadURL &&
                            actionButtons &&
                            actionButtons?.map(({ id, onClick }) => (
                              <Button
                                key={id}
                                variant="contained"
                                color="primary"
                                sx={{
                                  px: 1.3,
                                  py: 1,
                                  margin: 0,
                                  "& .MuiButton-startIcon": { margin: 0 },
                                }}
                                onClick={() => onClick(row.DownloadURL)}
                                startIcon={
                                  <FileDownloadOutlinedIcon
                                    sx={{ width: "27px", height: "auto" }}
                                  />
                                }
                              />
                            ))}
                        </Stack>
                      </Card>
                    </Grid>
                  )}
                  {mediaType === dataConstant.MediaCategoryTypes.Pdf && (
                    <Grid key={rowIndex} item size={{ xs: 12, sm: 6, lg: 4 }}>
                      <Card
                        key={rowIndex}
                        sx={(theme) => ({
                          background: theme.palette.common.black,
                          borderRadius: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 0,
                          width: "100%",
                          flexDirection: "column",
                          height: "100%",
                        })}
                      >
                        <Stack
                          direction={"row"}
                          gap={1}
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"flex-end"}
                          flexWrap={"wrap"}
                          p={"16px 20px"}
                          borderBottom={"1px solid var(--headerbg)"}
                          minHeight={{ xs: "auto", sm: "73px" }}
                        >
                          {row.DisplayTrainingDate && (
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={2}
                            >
                              <Card
                                sx={(theme) => ({
                                  background: theme.palette.secondary.headerbg,
                                  borderRadius: "10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 0,
                                  width: "100%",
                                  flexDirection: "column",
                                  height: "100%",
                                  p: 1,
                                })}
                              >
                                <img src="/icons/calendar.svg" alt="user" />
                              </Card>

                              <Typography
                                color="secondary.text"
                                variant="body1"
                              >
                                {moment(row.TrainingDate || new Date()).format(
                                  "MM/DD/YYYY"
                                )}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                        <Stack className="cm-media-inner pdf-inner">
                          {pdfButtons?.map(({ onClick }) => (
                            <img
                              key={row.Id}
                              className="gallery-image-card pdf-image"
                              width={"100%"}
                              src={`${process.env.REACT_APP_BLOB_URL}/backoffice-assets/pdf-icon.png`}
                              alt="user"
                              onClick={() => onClick(row.Id)}
                            />
                          ))}
                        </Stack>
                        <Stack
                          direction={"row"}
                          sx={{ gap: { xs: 1, sm: 4 } }}
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          p={"16px 20px"}
                          minHeight={{ xs: "auto", sm: "75px" }}
                          borderTop={"1px solid var(--headerbg)"}
                        >
                          <Typography variant="body1" sx={{wordBreak:"break-word"}} color="secondary.text">
                            {row.MediaDescription}
                          </Typography>
                          {row.DownloadURL &&
                            actionButtons &&
                            actionButtons?.map(({ id, onClick }) => (
                              <Button
                                key={id}
                                variant="contained"
                                color="primary"
                                sx={{
                                  px: 1.3,
                                  py: 1,
                                  margin: 0,
                                  "& .MuiButton-startIcon": { margin: 0 },
                                }}
                                onClick={() => onClick(row.DownloadURL)}
                                startIcon={
                                  <FileDownloadOutlinedIcon
                                    sx={{ width: "27px", height: "auto" }}
                                  />
                                }
                              />
                            ))}
                        </Stack>
                      </Card>
                    </Grid>
                  )}
                  {mediaType === dataConstant.MediaCategoryTypes.Video && (
                    <Grid key={rowIndex} item size={{ xs: 12, sm: 6, lg: 4 }}>
                      <Card
                        key={rowIndex}
                        sx={(theme) => ({
                          background: theme.palette.common.black,
                          borderRadius: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 0,
                          width: "100%",
                          flexDirection: "column",
                          height: "100%",
                        })}
                      >
                        <Stack
                          direction={"row"}
                          gap={1}
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"flex-end"}
                          flexWrap={"wrap"}
                          p={"16px 20px"}
                          borderBottom={"1px solid var(--headerbg)"}
                          minHeight={{ xs: "auto", sm: "73px" }}
                        >
                          {row.DisplayTrainingDate && (
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={2}
                            >
                              <Card
                                sx={(theme) => ({
                                  background: theme.palette.secondary.headerbg,
                                  borderRadius: "10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 0,
                                  width: "100%",
                                  flexDirection: "column",
                                  height: "100%",
                                  p: 1,
                                })}
                              >
                                <img src="/icons/calendar.svg" alt="user" />
                              </Card>

                              <Typography
                                color="secondary.text"
                                variant="body1"
                              >
                                {moment(row.TrainingDate || new Date()).format(
                                  "MM/DD/YYYY"
                                )}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>

                        <iframe
                          key={row.Id}
                          title={row.Id}
                          src={row.EmbededVideoLink}
                          border="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          className="cm-media-video"
                        />

                        <Stack
                          direction={"row"}
                          sx={{ gap: { xs: 1, sm: 4 } }}
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          p={"16px 20px"}
                          minHeight={{ xs: "auto", sm: "75px" }}
                          borderTop={"1px solid var(--headerbg)"}
                        >
                          <Typography variant="body1" sx={{wordBreak:"break-word"}} color="secondary.text">
                            {row.MediaDescription}
                          </Typography>
                          {row.DownloadURL &&
                            actionButtons &&
                            actionButtons?.map(({ id, onClick }) => (
                              <Button
                                key={id}
                                variant="contained"
                                color="primary"
                                sx={{
                                  px: 1.3,
                                  py: 1,
                                  margin: 0,
                                  "& .MuiButton-startIcon": { margin: 0 },
                                }}
                                onClick={() => onClick(row.DownloadURL)}
                                startIcon={
                                  <FileDownloadOutlinedIcon
                                    sx={{ width: "27px", height: "auto" }}
                                  />
                                }
                              />
                            ))}
                        </Stack>
                      </Card>
                    </Grid>
                  )}
                  {mediaType === dataConstant.MediaCategoryTypes.TextBlog && (
                    <Grid key={rowIndex} item size={{ xs: 12, sm: 6, lg: 4 }}>
                      <Card
                        key={rowIndex}
                        sx={(theme) => ({
                          background: theme.palette.common.black,
                          borderRadius: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 0,
                          width: "100%",
                          flexDirection: "column",
                          height: "100%",
                        })}
                      >
                        <Stack
                          direction={"row"}
                          gap={1}
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"flex-end"}
                          flexWrap={"wrap"}
                          p={"16px 20px"}
                          borderBottom={"1px solid var(--headerbg)"}
                          minHeight={{ xs: "auto", sm: "73px" }}
                        >
                          {row.DisplayTrainingDate && (
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={2}
                            >
                              <Card
                                sx={(theme) => ({
                                  background: theme.palette.secondary.headerbg,
                                  borderRadius: "10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 0,
                                  width: "100%",
                                  flexDirection: "column",
                                  height: "100%",
                                  p: 1,
                                })}
                              >
                                <img src="/icons/calendar.svg" alt="user" />
                              </Card>

                              <Typography
                                color="secondary.text"
                                variant="body1"
                              >
                                {moment(row.TrainingDate || new Date()).format(
                                  "MM/DD/YYYY"
                                )}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                        <Stack className="cm-media-inner">
                          <Box
                            sx={{
                              height: "auto",
                              overflowY: "auto",
                              wordWrap: "break-word",
                              width: "100%",
                              mt: 0,
                              p: "15px",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: row.MediaContent,
                            }}
                          />
                        </Stack>
                        <Stack
                          direction={"row"}
                          sx={{ gap: { xs: 1, sm: 4 } }}
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          p={"16px 20px"}
                          minHeight={{ xs: "auto", sm: "75px" }}
                          borderTop={"1px solid var(--headerbg)"}
                        >
                          <Typography variant="body1" sx={{wordBreak:"break-word"}} color="secondary.text">
                            {row.MediaDescription}
                          </Typography>
                          {row.DownloadURL &&
                            actionButtons &&
                            actionButtons?.map(({ id, onClick }) => (
                              <Button
                                key={id}
                                variant="contained"
                                color="primary"
                                sx={{
                                  px: 1.3,
                                  py: 1,
                                  margin: 0,
                                  "& .MuiButton-startIcon": { margin: 0 },
                                }}
                                onClick={() => onClick(row.DownloadURL)}
                                startIcon={
                                  <FileDownloadOutlinedIcon
                                    sx={{ width: "27px", height: "auto" }}
                                  />
                                }
                              />
                            ))}
                        </Stack>
                      </Card>
                    </Grid>
                  )}
                </React.Fragment>
              );
            })}
          </Grid>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <>
          <Stack
            direction={"row"}
            gap={1}
            width={"100%"}
            alignItems={"center"}
            justifyContent={"space-between"}
            flexWrap={"wrap"}
          >
            <Pagination
              className="cm-pagination"
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              variant="filled"
              color="secondary"
              disabled={loading}
              shape="rounded"
            />
          </Stack>
        </>
      )}
      <Stack
        direction={"row"}
        gap={"20px"}
        width={"100%"}
        justifyContent={"space-between"}
        alignItems={"center"}
        flexWrap={"wrap"}
      >
        <Button
          variant="outlined"
          color="secondary"
          sx={{ px: 3 }}
          startIcon={
            <KeyboardBackspaceOutlinedIcon
              sx={{ color: "var(--primary-main)" }}
            />
          }
          onClick={onBack}
        >
          {t("Media.Back")}
        </Button>

        {!loading && data.length != 0 && buttonName && (
          <Box>
            <Button
              variant="contained"
              color="primary"
              sx={{ px: 3 }}
              onClick={handleCompleteSurvey}
            >
              {buttonName}
            </Button>
          </Box>
        )}
      </Stack>

      <Box textAlign="center" width={"100%"} borderBottom={0} mt={"20px"}>
        <Typography variant="body2">
          {t("Media.Showing")} {data.length} {t("Media.Of")} {totalCount}{" "}
          {t("Media.Records")}
        </Typography>
      </Box>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};

MediaCardGrid.propTypes = {
  title: PropTypes.string,
  defaultItemsPerPage: PropTypes.number,
  enableSearch: PropTypes.bool,
  onDataChange: PropTypes.func,
  actionButtons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    })
  ),
  pdfButtons: PropTypes.arrayOf(
    PropTypes.shape({
      onClick: PropTypes.func.isRequired,
    })
  ),
  onFetchData: PropTypes.func,
  value: PropTypes.number,
  mediaType: PropTypes.oneOf([
    dataConstant.MediaCategoryTypes.Pdf,
    dataConstant.MediaCategoryTypes.Picture,
    dataConstant.MediaCategoryTypes.Video,
    dataConstant.MediaCategoryTypes.TextBlog,
  ]),
};
export default MediaCardGrid;
